import { FC, MouseEventHandler, useMemo } from "react";
import { Contact } from "lucide-react";

import {
  CampaignCardDefault,
  CampaignCardLabelBooleanI,
  CampaignCardLabelValueI,
} from "shared/ui/cards/campaign-card/campaign-card-default";
import { CampaignCardDescription } from "shared/ui/cards/campaign-card/shared/campaign-card-description";
import { CampaignCardStats } from "shared/ui/cards/campaign-card/shared/campaign-card-stats";
import { CampaignCardFeatures } from "shared/ui/cards/campaign-card/shared/campaign-card-features";
import { CampaignCardStatusPills } from "shared/ui/cards/campaign-card/shared/campaign-card-status-pills";
import { CampaignCardExtended } from "shared/ui/cards/campaign-card/campaign-card-extended";
import { CampaignCardPerformance } from "shared/ui/cards/campaign-card/shared/campaign-card-performance";
import { CampaignCardActions } from "shared/ui/cards/campaign-card/shared/campaign-card-actions";
import { CampaignCardMini } from "shared/ui/cards/campaign-card/campaign-card-mini";
import { CampaignCardHighlights } from "shared/ui/cards/campaign-card/campaign-card-highlights";
import {
  CampaignUserStatusI,
  CampaignV3I,
} from "shared/lib/interfaces/campaign";
import {
  DayJs,
  convertTimeToNormalizedDuration,
  safeGetDate,
} from "shared/lib/helpers/date";
import { STATUS_TYPES } from "shared/lib/constants/statuses";
import { CAMPAIGN_USER_STATUS } from "shared/lib/constants/campaign";
import { StatusPill } from "shared/ui/pills";
import { CampaignCardUpcomingDate } from "shared/ui/cards/campaign-card/shared/campaign-card-upcoming-date";
import { clsxMerge } from "shared/lib/helpers";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import {
  formatMoneyCents,
  formatMoneyDollars,
} from "shared/lib/helpers/numbers";

import { CampaignCardLabels } from "./shared/campaign-card-labels";
import { CampaignCardIndustryPill } from "./shared/campaign-card-labels/campaign-card-industry-pill";
import { CampaignCardTrainingPill } from "./shared/campaign-card-labels/campaign-card-training-pill";
import { CampaignCardWorkspacePill } from "./shared/campaign-card-labels/campain-card-workspace-pill";

const getFormattedTimeAuditionResponseTime = (numHours: number) => {
  const { days, hours, minutes } = convertTimeToNormalizedDuration({
    hours: numHours,
  });

  let res = "";

  res += days ? `${days} days ` : "";
  res += hours ? `${hours}h ` : "";
  res += minutes ? `${minutes}m` : "";

  return res.length > 0 ? res : "Less than 1 hour";
};

const EFFECTIVE_DATE_FORMAT = "MM/DD/YYYY";

export const CAMPAIGN_CARD_TYPES = {
  DEFAULT: "default",
  EXTENDED: "extended",
  MINI: "mini",
  HIGHLIGHTS: "highlights",
  OPEN_MARKETPLACE: "open_marketplace",
} as const;

export const CAMPAIGN_CARD_PERFORMANCE_TYPES = {
  QUALIFIED_MEETINGS_BOOKED: "qualified_meetings_booked",
  CALLS_MADE: "calls_made",
  HOURS_SPENT_ON_CAMPAIGN: "hours_spent_on_campaign",
  SHOW_UP_RATE: "show_up_rate",
} as const;

export const CAMPAIGN_CARD_PERFORMANCE_TYPES_MAP = {
  [CAMPAIGN_CARD_PERFORMANCE_TYPES.QUALIFIED_MEETINGS_BOOKED]:
    "Qualified Meetings Booked",
  [CAMPAIGN_CARD_PERFORMANCE_TYPES.CALLS_MADE]: "Calls Made",
  [CAMPAIGN_CARD_PERFORMANCE_TYPES.HOURS_SPENT_ON_CAMPAIGN]:
    "Hours Spent On Campaign",
  [CAMPAIGN_CARD_PERFORMANCE_TYPES.SHOW_UP_RATE]: "Show Up Rate",
} as const;

export interface CampaignCardI extends PropsWithClassNameI {
  type?: ValueOfObjectFields<typeof CAMPAIGN_CARD_TYPES>;
  logo_s3_url?: string;
  name?: string;
  created_at?: string;

  lowest_payout_in_usd?: string | number;
  highest_payout_in_usd?: string | number;
  weekly_payout_in_usd?: number;
  all_time_payout_in_usd?: number;

  hold_rate_numerator?: number;
  hold_rate_denominator?: number;
  all_time_hold_rate_numerator?: number;
  all_time_hold_rate_denominator?: number;

  industry?: string;
  elevator_pitch?: string;
  eligible_lead_count?: number;
  weekly_active_caller_count?: number;
  is_self_sourced_banned?: boolean;
  campaign_user_status?: CampaignUserStatusI;
  job_description?: CampaignV3I["job_description"];

  dialer_toggle_off_time?: string;
  direct_call_toggle_off_time?: string;
  startCallingUrl?: string;
  assessmentUrl?: string;
  handleOpenSalesFloor?: MouseEventHandler;
  my_qualified_meetings_booked?: number;
  my_calls_made?: number;
  my_hours_spent_on_campaign?: number;
  my_show_up_rate?: number;
  average_audition_response_time_in_hours?: number;
  audition_acceptance_rate?: number;
  steps_complete?: number;
  total_steps?: number;

  booked_meetings_count?: number;
  qualified_meetings_count?: number;
  is_weekend_eligible?: boolean;
  successful_pitch?: { duration?: number; recording_url?: string };

  containerClassName?: string;

  base_payout?: number;
  bonus_payout?: number;
  bonus_2_payout?: number;

  workspace_name?: string;
  workspace_campaign_count?: number;

  circle_space_slug?: string | null;
  circle_space_group_slug?: string | null;

  onClick?: () => void;
  onClickTraining?: () => Promise<void>;
  onClickLeaveCampaign?: () => Promise<void>;

  isFlipEnabled?: boolean;
}

const createLabelValueObject = (
  label: string,
  value: string | number,
  accent?: boolean
) => ({
  label,
  value,
  accent,
});

const createLabelBooleanObject = (label: string, value: boolean) => ({
  label,
  value,
});
/**
 * Example
 * const MOCK_CAMPAIGN_DATA: CampaignV3I = {
  name: "Redpanda",
  logo_s3_url: "",
  hold_rate_numerator: 0,
  hold_rate_denominator: 1,
  weekly_active_caller_count: 50,
  eligible_lead_count: 10000,
  industry: "SaaS",
  lowest_payout_in_usd: 500,
  highest_payout_in_usd: 500,
  weekly_payout_in_usd: 2000,
  elevator_pitch:
    "Redpanda is a simple, powerful, and cost-efficient streaming data platform that is compatible with Kafka® APIs while...",
  job_description: {
    id: "123",
    created_at: "",
    updated_at: "",
    url: "https://www.glencoco.com/",
  },
  is_self_sourced_banned: true,
};
 * @returns 
 */
export const CampaignCard: FC<CampaignCardI> = ({
  type = CAMPAIGN_CARD_TYPES.DEFAULT,
  logo_s3_url,
  name,
  created_at = "",
  lowest_payout_in_usd,
  highest_payout_in_usd,
  // all_time_payout_in_usd,
  industry,
  elevator_pitch,
  weekly_active_caller_count,
  eligible_lead_count,
  all_time_hold_rate_numerator,
  all_time_hold_rate_denominator,
  is_self_sourced_banned,
  campaign_user_status,
  job_description,

  dialer_toggle_off_time,
  direct_call_toggle_off_time,
  startCallingUrl,
  assessmentUrl,
  handleOpenSalesFloor,
  my_qualified_meetings_booked,
  my_calls_made,
  my_hours_spent_on_campaign,
  my_show_up_rate,
  average_audition_response_time_in_hours,
  audition_acceptance_rate,
  steps_complete = 0,
  total_steps = 0,

  successful_pitch,
  booked_meetings_count,
  qualified_meetings_count,
  is_weekend_eligible,

  base_payout,
  bonus_payout,
  bonus_2_payout,

  workspace_name,
  workspace_campaign_count,

  className,
  containerClassName,

  onClick = () => {},
  onClickTraining,
  onClickLeaveCampaign,

  isFlipEnabled = true,
}) => {
  const isDialerInfoMessage =
    !!dialer_toggle_off_time && DayJs().isBefore(dialer_toggle_off_time, "day");

  const isDirectCallInfoMessage =
    !!direct_call_toggle_off_time &&
    DayJs().isBefore(dialer_toggle_off_time, "day");

  const shouldShowEffectiveDates =
    isDialerInfoMessage || isDirectCallInfoMessage;

  const accentStats: Array<CampaignCardLabelValueI> = useMemo(
    () => [
      createLabelValueObject(
        "Audition Response Time",
        average_audition_response_time_in_hours &&
          average_audition_response_time_in_hours > 0
          ? getFormattedTimeAuditionResponseTime(
              average_audition_response_time_in_hours
            )
          : "-",
        true
      ),
      createLabelValueObject(
        "Acceptance Rate",
        audition_acceptance_rate && audition_acceptance_rate > 0
          ? `${Math.floor(audition_acceptance_rate * 100)}%`
          : "-",
        true
      ),
    ],
    [average_audition_response_time_in_hours, audition_acceptance_rate]
  );
  const basicStats: Array<CampaignCardLabelValueI> = useMemo(
    () => [
      createLabelValueObject(
        "Eligible Lead Count",
        eligible_lead_count as number
      ),
    ],
    [
      eligible_lead_count,
      weekly_active_caller_count,
      all_time_hold_rate_numerator,
      all_time_hold_rate_denominator,
    ]
  );

  const stats: Array<CampaignCardLabelValueI> = useMemo(
    () => [...accentStats, ...basicStats],
    [accentStats, basicStats]
  );

  const features: Array<CampaignCardLabelBooleanI> = useMemo(
    () => [
      createLabelBooleanObject(
        "Campaign provided leads",
        !dialer_toggle_off_time
      ),
      createLabelBooleanObject("Self sourced leads", !is_self_sourced_banned),
    ],
    [is_self_sourced_banned, job_description, dialer_toggle_off_time]
  );

  const myPerformanceData = useMemo(
    () => [
      createLabelValueObject(
        CAMPAIGN_CARD_PERFORMANCE_TYPES_MAP[
          CAMPAIGN_CARD_PERFORMANCE_TYPES.QUALIFIED_MEETINGS_BOOKED
        ],
        my_qualified_meetings_booked as number
      ),
      createLabelValueObject(
        CAMPAIGN_CARD_PERFORMANCE_TYPES_MAP[
          CAMPAIGN_CARD_PERFORMANCE_TYPES.CALLS_MADE
        ],
        my_calls_made as number
      ),
      createLabelValueObject(
        CAMPAIGN_CARD_PERFORMANCE_TYPES_MAP[
          CAMPAIGN_CARD_PERFORMANCE_TYPES.HOURS_SPENT_ON_CAMPAIGN
        ],
        my_hours_spent_on_campaign as number
      ),
      createLabelValueObject(
        CAMPAIGN_CARD_PERFORMANCE_TYPES_MAP[
          CAMPAIGN_CARD_PERFORMANCE_TYPES.SHOW_UP_RATE
        ],
        my_show_up_rate as number
      ),
    ],
    [
      my_qualified_meetings_booked,
      my_calls_made,
      my_hours_spent_on_campaign,
      my_show_up_rate,
    ]
  );

  // This is a legacy "tiered" way of calculating the price, we must eventually
  // get rid of this and use the base_payout and bonus_payout fields.
  const price = useMemo(() => {
    return lowest_payout_in_usd === highest_payout_in_usd
      ? `$${lowest_payout_in_usd}`
      : `$${lowest_payout_in_usd} - $${highest_payout_in_usd}`;
  }, [lowest_payout_in_usd, highest_payout_in_usd]);

  const commonPriceProps = useMemo(() => {
    const formatPayout = (amount: number) =>
      formatMoneyCents(amount, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

    const basePrice = base_payout ? formatPayout(base_payout) : price;

    /* Three-tier campaigns offer two mutually exclusive bonuses, so "+ $X
     * Bonus" would have to pick one and understate the other. Show the span
     * from base to the best achievable payout instead.
     *
     * The ceiling comes from highest_payout_in_usd, which the backend derives
     * with Bounty.MaxUserPayout. Adding base_payout to a bonus payout here
     * would be wrong: each arrives already floored to the nearest $25 on its
     * own, and the real figure floors the combined amount once, so the sum can
     * understate it by up to $25. Only fall back to it if the range is absent.
     *
     * One- and two-tier campaigns are untouched: without a second bonus this
     * falls through to the existing base + bonus rendering. */
    if (base_payout && bonus_2_payout) {
      const bestBonus = Math.max(bonus_payout || 0, bonus_2_payout);
      // highest_payout_in_usd is already in dollars, base/bonus payouts are in
      // cents, so the two sides need different formatters to end up looking
      // alike. Both must group thousands or the range reads "$700 - $1125".
      const ceiling = highest_payout_in_usd
        ? formatMoneyDollars(highest_payout_in_usd, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
        : formatPayout(base_payout + bestBonus);

      return {
        price: `${basePrice} - ${ceiling}`,
        bonusPrice: "",
      };
    }

    return {
      price: basePrice,
      bonusPrice: bonus_payout ? formatPayout(bonus_payout) : "",
    };
  }, [base_payout, price, bonus_payout, bonus_2_payout, highest_payout_in_usd]);

  const dialerInfoMessage = useMemo(() => {
    return `No dialer use will be allowed starting ${safeGetDate(
      dialer_toggle_off_time as string,
      EFFECTIVE_DATE_FORMAT
    )}`;
  }, [dialer_toggle_off_time]);

  const directCallInfoMessage = useMemo(() => {
    return `No callbacks will be allowed starting ${safeGetDate(
      direct_call_toggle_off_time as string,
      EFFECTIVE_DATE_FORMAT
    )}`;
  }, [direct_call_toggle_off_time]);

  return (
    <div className={containerClassName}>
      {type === CAMPAIGN_CARD_TYPES.DEFAULT && (
        <CampaignCardDefault
          imgSrc={logo_s3_url}
          title={name}
          status={
            <CampaignCardStatusPills
              created_at={created_at}
              campaign_user_status={campaign_user_status}
              job_description={job_description}
              steps_complete={steps_complete}
              total_steps={total_steps}
            />
          }
          upcomingDates={
            <CampaignCardUpcomingDate
              shouldShowEffectiveDates={shouldShowEffectiveDates}
              dialerInfoMessage={dialerInfoMessage}
              directCallInfoMessage={directCallInfoMessage}
              isDialerInfoMessage={isDialerInfoMessage}
              isDirectCallInfoMessage={isDirectCallInfoMessage}
            />
          }
          {...commonPriceProps}
          priceDescription={undefined}
          labels={
            <CampaignCardLabels
              labels={[
                <CampaignCardIndustryPill industry={industry as string} />,
                <CampaignCardWorkspacePill
                  workspaceName={workspace_name}
                  workspaceCampaignCount={workspace_campaign_count}
                />,
              ]}
            />
          }
          description={<CampaignCardDescription text={elevator_pitch} />}
          stats={<CampaignCardStats stats={stats} />}
          features={<CampaignCardFeatures features={features} />}
          onClick={onClick}
          className={className}
        />
      )}

      {type === CAMPAIGN_CARD_TYPES.EXTENDED && (
        <CampaignCardExtended
          imgSrc={logo_s3_url}
          title={name}
          status={
            <CampaignCardStatusPills
              created_at={created_at}
              campaign_user_status={campaign_user_status}
              job_description={job_description}
              steps_complete={steps_complete}
              total_steps={total_steps}
            />
          }
          upcomingDates={
            <CampaignCardUpcomingDate
              shouldShowEffectiveDates={shouldShowEffectiveDates}
              dialerInfoMessage={dialerInfoMessage}
              directCallInfoMessage={directCallInfoMessage}
              isDialerInfoMessage={isDialerInfoMessage}
              isDirectCallInfoMessage={isDirectCallInfoMessage}
            />
          }
          {...commonPriceProps}
          priceDescription={undefined}
          labels={
            <CampaignCardLabels
              labels={[
                <CampaignCardIndustryPill industry={industry as string} />,
                <CampaignCardWorkspacePill
                  workspaceName={workspace_name}
                  workspaceCampaignCount={workspace_campaign_count}
                />,
              ]}
            />
          }
          description={<CampaignCardDescription text={elevator_pitch} />}
          stats={<CampaignCardStats stats={stats} />}
          features={<CampaignCardFeatures features={features} />}
          performance={<CampaignCardPerformance stats={myPerformanceData} />}
          actions={
            <CampaignCardActions
              campaign_user_status={campaign_user_status}
              startCallingUrl={startCallingUrl}
              assessmentUrl={assessmentUrl}
              handleOpenSalesFloor={handleOpenSalesFloor}
              onClickTraining={onClickTraining}
              onClickLeaveCampaign={onClickLeaveCampaign}
            />
          }
          onClick={onClick}
          className={className}
        />
      )}

      {type === CAMPAIGN_CARD_TYPES.MINI && (
        <CampaignCardMini
          imgSrc={logo_s3_url}
          title={name}
          {...commonPriceProps}
          priceDescription={undefined}
          labels={
            <CampaignCardLabels
              labels={[
                <CampaignCardIndustryPill industry={industry as string} />,
                <CampaignCardWorkspacePill
                  workspaceName={workspace_name}
                  workspaceCampaignCount={workspace_campaign_count}
                />,
                CAMPAIGN_USER_STATUS.AUDITION_IN_REVIEW ===
                campaign_user_status ? (
                  <StatusPill status={STATUS_TYPES.PENDING} />
                ) : (
                  <></>
                ),
                steps_complete < total_steps ? (
                  <CampaignCardTrainingPill
                    steps_complete={steps_complete}
                    total_steps={total_steps}
                  />
                ) : (
                  <></>
                ),
              ]}
            />
          }
          actions={
            <CampaignCardActions
              stacked
              campaign_user_status={campaign_user_status}
              startCallingUrl={startCallingUrl}
              assessmentUrl={assessmentUrl}
              handleOpenSalesFloor={handleOpenSalesFloor}
              onClickTraining={onClickTraining}
              onClickLeaveCampaign={onClickLeaveCampaign}
            />
          }
          onClick={onClick}
          className={className}
        />
      )}

      {type === CAMPAIGN_CARD_TYPES.HIGHLIGHTS && (
        <CampaignCardHighlights
          imgSrc={logo_s3_url}
          title={name}
          isFlipEnabled={isFlipEnabled}
          labels={
            <div className="flex flex-col">
              <CampaignCardLabels
                className="flex-col"
                labels={[
                  <CampaignCardIndustryPill
                    industry={industry as string}
                    className={"mr-0"}
                  />,
                ]}
              />
            </div>
          }
          status={
            <CampaignCardStatusPills
              created_at={created_at}
              campaign_user_status={campaign_user_status}
              job_description={job_description}
              steps_complete={steps_complete}
              total_steps={total_steps}
            />
          }
          {...commonPriceProps}
          priceDescription={
            <div
              className={clsxMerge(
                "flex items-center gap-1 rounded-lg bg-[#2CA83114] p-3"
              )}
            >
              <Contact size={16} className="mr-2" /> Eligible lead count:{" "}
              <span className="typography-body-4">{eligible_lead_count}</span>
            </div>
          }
          stats={
            <CampaignCardStats
              className="w-full"
              statsClassName="w-full"
              stats={accentStats}
              hideBasicStats
            />
          }
          successfulPitchRecordingURL={successful_pitch?.recording_url}
          eligibleLeads={eligible_lead_count}
          weeklyActiveCallers={weekly_active_caller_count}
          bookedMeetings={booked_meetings_count}
          qualifiedMeetings={qualified_meetings_count}
          isWeekendEligible={is_weekend_eligible}
          onClick={onClick}
          className={className}
        />
      )}
    </div>
  );
};
